import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DrTestRow {
  readonly drTestId: string;
  readonly testCode: string;
  readonly testName: string;
  readonly testType: string;
  readonly status: string;
  readonly rtoMet: boolean | null;
  readonly rpoMet: boolean | null;
}

const TYPES = ['', 'TABLETOP', 'BACKUP_RESTORE', 'FAILOVER', 'FAILBACK', 'DR_DRILL', 'BC_SIMULATION', 'CYBER_RECOVERY'];
const STATUSES = ['', 'SCHEDULED', 'IN_PROGRESS', 'PASSED', 'FAILED', 'PARTIAL'];
const SCHEDULE_TYPES = TYPES.filter(Boolean);
const RESULT_STATUSES = ['PASSED', 'FAILED', 'PARTIAL'];

const met = (v: boolean | null) => (v === null ? '—' : v ? 'MET' : 'MISSED');

/**
 * M31 — Testing Dashboard (§20 / §31). Recovery-testing register by test type
 * with the exercise status and achieved RTO/RPO outcome (BR-04: periodic tests
 * are mandatory; a FAILED result raises a DR-test failure). Supports scheduling
 * a new test, starting it and recording its result — each wired to the guarded
 * /v1/bcm/dr-tests write API. Filterable by test type and status.
 */
export function TestingDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<DrTestRow[]>([]);
  const [testType, setTestType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('DR_DRILL');
  const [resultStatus, setResultStatus] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (testType) params.set('testType', testType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: DrTestRow[] }>(`/v1/bcm/dr-tests${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, testType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m31.testing.err403')
      : (err as Error).message;

  const schedule = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m31.testing.errName')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/dr-tests`, {
        method: 'POST',
        body: { testName: newName.trim(), testType: newType },
        token,
      });
      setNotice(tr('m31.testing.noticeScheduled', { name: newName.trim() }));
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: DrTestRow, path: string, label: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/dr-tests/${r.drTestId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(tr('m31.testing.actNotice', { code: r.testCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  const recordResult = async (r: DrTestRow): Promise<void> => {
    const s = resultStatus[r.drTestId] ?? 'PASSED';
    await act(r, 'result', tr('m31.testing.resultLabel', { status: s }), { status: s });
  };

  return (
    <main aria-label="testing-dashboard">
      <h1>{tr('m31.testing.title')}</h1>

      <section aria-label="schedule-test">
        <h2>{tr('m31.testing.scheduleHeading')}</h2>
        <form aria-label="test-form" onSubmit={(e) => void schedule(e)}>
          <label>{tr('m31.testing.fieldName')}<input aria-label="field-test-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{tr('m31.testing.fieldType')}
            <select aria-label="field-test-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {SCHEDULE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-test" disabled={busy}>{busy ? tr('m31.testing.busySchedule') : tr('m31.testing.submitSchedule')}</button>
        </form>
        {actionError && <p role="alert" aria-label="test-error">{actionError}</p>}
        {notice && <p aria-label="test-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m31.testing.filterType')}
          <select aria-label="filter-test-type" value={testType} onChange={(e) => setTestType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m31.testing.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m31.testing.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m31.testing.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m31.testing.loading')}</p> : (
        <>
          <p aria-label="test-count">{tr('m31.testing.count', { count: rows.length })}</p>
          <table aria-label="test-table">
            <thead><tr><th>{tr('m31.testing.thCode')}</th><th>{tr('m31.testing.thName')}</th><th>{tr('m31.testing.thType')}</th><th>{tr('m31.testing.thStatus')}</th><th>{tr('m31.testing.thRto')}</th><th>{tr('m31.testing.thRpo')}</th><th>{tr('m31.testing.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.drTestId} data-status={r.status}>
                  <td>{r.testCode}</td><td>{r.testName}</td><td>{r.testType}</td>
                  <td>{r.status}</td>
                  <td aria-label={`rto-${r.drTestId}`}>{met(r.rtoMet)}</td>
                  <td aria-label={`rpo-${r.drTestId}`}>{met(r.rpoMet)}</td>
                  <td>
                    {r.status === 'SCHEDULED' && (
                      <button type="button" aria-label={`start-${r.testCode}`} onClick={() => void act(r, 'start', tr('m31.testing.inProgress'))}>{tr('m31.testing.start')}</button>
                    )}
                    {r.status === 'IN_PROGRESS' && (
                      <>
                        <select aria-label={`result-status-${r.testCode}`} value={resultStatus[r.drTestId] ?? 'PASSED'} onChange={(e) => setResultStatus((m) => ({ ...m, [r.drTestId]: e.target.value }))}>
                          {RESULT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button type="button" aria-label={`result-${r.testCode}`} onClick={() => void recordResult(r)}>{tr('m31.testing.recordResult')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
